/*
 * Superette motion. Declarative: sections carry attributes, this file reads
 * them. Motion stays declarative everywhere - no section ships its own
 * animation code. The one per-section script in the theme is
 * assets/superette-book.js, and it is form behaviour rather than motion:
 * focusing a confirmation and restoring fields after a validation error.
 *
 *   data-motion-enter               plays once on load, no scroll trigger
 *   data-motion-reveal              rises into place every time it enters view
 *   data-motion-delay="0.08"        seconds, offsets an enter or reveal
 *   data-motion-stagger="0.08"      on a container; auto-delays its children
 *   data-motion-sheen               pointer-tracked highlight, writes --mx/--my
 *   data-motion-tilt                pointer-driven 3D tilt
 *   data-motion-tilt-max="9"        tilt limit in degrees
 *   data-motion-parallax            scroll-linked translate
 *   data-motion-speed="0.07"        parallax strength
 *   data-motion-marquee="38"        continuous horizontal drift, seconds
 *   data-motion-progress            scaleX 0 to 1 across page scroll
 *   data-motion-press               spring press feedback
 *   data-motion-drift="18"          slow looping drift, seconds (bloom)
 *   data-motion-drift-x / -y        drift distance, percent of own size
 *
 * Progressive enhancement: resting states live in CSS behind
 * html.motion-enabled, set by the inline script in layout/superette.liquid only
 * when JS runs and reduced motion is not requested. No JS, reduced motion, or a
 * failed asset means the class is absent and content is simply already in
 * place. Nothing here is load-bearing for legibility.
 *
 * Motion, not CSS keyframes, drives everything except the footer shimmer.
 * superette-base.css clamps animation and transition duration under reduced
 * motion, so CSS-driven motion would silently die there.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  if (!root.classList.contains('motion-enabled')) return;

  var M = window.Motion;
  if (!M || typeof M.animate !== 'function') {
    root.classList.remove('motion-enabled');
    return;
  }

  // Mirrors --motion-ease / --motion-slow in superette-tokens.css.
  var EASE = [0.16, 1, 0.3, 1];
  var DURATION = 0.7;
  var STAGGER_STEP = 0.08;

  /* ---------------------------------------------------------------- stagger */

  function applyStagger(scope) {
    var containers = scope.querySelectorAll('[data-motion-stagger]');
    for (var i = 0; i < containers.length; i++) {
      var step = parseFloat(containers[i].getAttribute('data-motion-stagger')) || STAGGER_STEP;
      var kids = containers[i].querySelectorAll('[data-motion-reveal], [data-motion-enter]');
      for (var j = 0; j < kids.length; j++) {
        if (!kids[j].hasAttribute('data-motion-delay')) {
          kids[j].setAttribute('data-motion-delay', (j * step).toFixed(3));
        }
      }
    }
  }

  /* ----------------------------------------------------------------- enter */

  /*
   * Load-triggered entrance, separate from reveal on purpose.
   *
   * Conceptually the hero is the page opening, not something scrolled to.
   * Practically, it must not depend on any visibility test: the hero words rise
   * out of a box that clips them, and anything clipped by an ancestor's
   * overflow:hidden reads as zero-area to a visibility check.
   */
  function initEnter(el) {
    if (el.dataset.motionBound) return;
    el.dataset.motionBound = '1';
    var delay = parseFloat(el.getAttribute('data-motion-delay')) || 0;
    M.animate(
      el,
      { opacity: 1, transform: 'translateY(0) scale(1)' },
      { duration: DURATION, delay: delay, ease: EASE }
    );
  }

  /* ---------------------------------------------------------------- reveals */

  /*
   * Driven by the shared scroll loop rather than IntersectionObserver.
   *
   * They repeat: an element animates in every time it enters view and resets
   * once fully clear, so the page keeps its life on the second and third pass
   * rather than only the first.
   *
   * It also removes a class of bug. IntersectionObserver cannot express
   * asymmetric thresholds, so one threshold either fires late on entry or
   * resets while part of the element is still on screen. Measuring rects each
   * frame makes stranding impossible: what is visible is shown, full stop.
   */
  var revealEntries = [];

  // Enter well inside the viewport, reset only once fully clear. The gap
  // between the two is what stops an element flickering at the edge.
  var ENTER_TOP = 0.88;
  var ENTER_BOTTOM = 0.06;
  var EXIT_MARGIN = 48;

  function initReveal(el) {
    if (el.dataset.motionBound) return;
    el.dataset.motionBound = '1';
    revealEntries.push({ el: el, shown: false, anim: null });
  }

  function showReveal(entry) {
    var delay = parseFloat(entry.el.getAttribute('data-motion-delay')) || 0;
    if (entry.anim && entry.anim.stop) entry.anim.stop();
    entry.anim = M.animate(
      entry.el,
      { opacity: 1, transform: 'translateY(0) scale(1)' },
      { duration: DURATION, delay: delay, ease: EASE }
    );
  }

  function hideReveal(entry) {
    if (entry.anim && entry.anim.stop) entry.anim.stop();
    entry.anim = null;
    // Clearing the inline styles Motion committed hands the element back to the
    // CSS resting state, so "hidden" has exactly one definition.
    entry.el.style.opacity = '';
    entry.el.style.transform = '';
  }

  function updateReveals(vh) {
    for (var i = 0; i < revealEntries.length; i++) {
      var entry = revealEntries[i];
      var r = entry.el.getBoundingClientRect();
      if (!entry.shown && r.top < vh * ENTER_TOP && r.bottom > vh * ENTER_BOTTOM) {
        entry.shown = true;
        showReveal(entry);
      } else if (entry.shown && (r.bottom < -EXIT_MARGIN || r.top > vh + EXIT_MARGIN)) {
        entry.shown = false;
        hideReveal(entry);
      }
    }
  }

  /* ----------------------------------------------------------------- sheen */

  function initSheen(el) {
    M.hover(el, function () {
      // Cached once per hover, not per pointer event: getBoundingClientRect
      // forces layout and this fires at pointer rate.
      var r = el.getBoundingClientRect();
      var w = r.width || 1;
      var h = r.height || 1;
      var left = r.left;
      var top = r.top;

      function move(event) {
        el.style.setProperty('--mx', (((event.clientX - left) / w) * 100).toFixed(2) + '%');
        el.style.setProperty('--my', (((event.clientY - top) / h) * 100).toFixed(2) + '%');
      }

      el.classList.add('is-sheen-active');
      el.addEventListener('pointermove', move, { passive: true });

      return function () {
        el.classList.remove('is-sheen-active');
        el.removeEventListener('pointermove', move);
      };
    });
  }

  /* ------------------------------------------------------------------ tilt */

  /*
   * Real perspective rather than a drop shadow pretending to be depth. The
   * element rotates about its own centre toward the pointer; the parent
   * supplies the perspective, so the further corner genuinely foreshortens.
   *
   * Applied to elements that carry no other transform - the media frame, not
   * the card - because reveal owns transform on the card and the two would
   * overwrite each other.
   */
  function initTilt(el) {
    var max = parseFloat(el.getAttribute('data-motion-tilt-max'));
    if (isNaN(max)) max = 9;

    M.hover(el, function () {
      var r = el.getBoundingClientRect();
      var w = r.width || 1;
      var h = r.height || 1;
      var left = r.left;
      var top = r.top;

      function move(event) {
        var px = (event.clientX - left) / w - 0.5;
        var py = (event.clientY - top) / h - 0.5;
        el.style.transform =
          'perspective(900px) rotateX(' + (-py * max).toFixed(2) +
          'deg) rotateY(' + (px * max).toFixed(2) + 'deg) translateZ(0)';
      }

      el.classList.add('is-tilting');
      el.addEventListener('pointermove', move, { passive: true });

      return function () {
        el.classList.remove('is-tilting');
        el.removeEventListener('pointermove', move);
        M.animate(
          el,
          { transform: 'perspective(900px) rotateX(0deg) rotateY(0deg) translateZ(0)' },
          { type: M.spring, bounce: 0.3, visualDuration: 0.5 }
        );
      };
    });
  }

  /* --------------------------------------------------------------- marquee */

  /*
   * Horizontal drift. The track holds the content twice, so translating it by
   * exactly -50% lands on an identical frame and the loop is seamless.
   */
  function initMarquee(el) {
    var duration = parseFloat(el.getAttribute('data-motion-marquee')) || 38;
    M.animate(
      el,
      { transform: ['translate3d(0, 0, 0)', 'translate3d(-50%, 0, 0)'] },
      { duration: duration, repeat: Infinity, ease: 'linear' }
    );
  }

  /* ----------------------------------------------------------------- press */

  function initPress(el) {
    M.press(el, function () {
      M.animate(el, { transform: 'scale(0.97)' }, { duration: 0.12, ease: 'easeOut' });
      return function () {
        M.animate(
          el,
          { transform: 'scale(1)' },
          { type: M.spring, bounce: 0.35, visualDuration: 0.35 }
        );
      };
    });
  }

  /* ----------------------------------------------------------------- drift */

  function initDrift(el, index) {
    var duration = parseFloat(el.getAttribute('data-motion-drift')) || 18;
    var dx = parseFloat(el.getAttribute('data-motion-drift-x'));
    var dy = parseFloat(el.getAttribute('data-motion-drift-y'));
    if (isNaN(dx)) dx = 8;
    if (isNaN(dy)) dy = 6;

    M.animate(
      el,
      { transform: ['translate3d(0%, 0%, 0)', 'translate3d(' + dx + '%, ' + dy + '%, 0)'] },
      {
        // Staggered durations rather than delays, so none of them sits still
        // while the page loads.
        duration: duration + index * 2.5,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut'
      }
    );
  }

  /* ------------------------------------- scroll loop: reveals, parallax, progress */

  var parallax = [];
  var progress = [];
  var ticking = false;
  var listening = false;

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(frame);
  }

  function frame() {
    ticking = false;

    var vh = window.innerHeight;
    var i;

    if (revealEntries.length) updateReveals(vh);

    for (i = 0; i < parallax.length; i++) {
      var p = parallax[i];
      var r = p.el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) continue; // offscreen, nothing to move
      var offset = (r.top + r.height / 2 - vh / 2) * p.speed;
      p.el.style.transform = 'translate3d(0,' + offset.toFixed(2) + 'px,0)';
    }

    if (progress.length) {
      var max = root.scrollHeight - vh;
      var ratio = max > 0 ? window.scrollY / max : 0;
      if (ratio < 0) ratio = 0;
      if (ratio > 1) ratio = 1;
      for (i = 0; i < progress.length; i++) {
        progress[i].style.transform = 'scaleX(' + ratio.toFixed(4) + ')';
      }
    }
  }

  function startScrollLoop() {
    if (listening) return;
    listening = true;
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    frame();
  }

  /* ------------------------------------------------------------------ init */

  function each(scope, selector, fn) {
    var nodes = scope.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) fn(nodes[i], i);
  }

  function initWithin(scope) {
    applyStagger(scope);

    each(scope, '[data-motion-enter]', initEnter);
    each(scope, '[data-motion-reveal]', initReveal);
    each(scope, '[data-motion-sheen]', initSheen);
    each(scope, '[data-motion-tilt]', initTilt);
    each(scope, '[data-motion-marquee]', initMarquee);
    each(scope, '[data-motion-press]', initPress);
    each(scope, '[data-motion-drift]', initDrift);

    each(scope, '[data-motion-parallax]', function (el) {
      parallax.push({
        el: el,
        speed: parseFloat(el.getAttribute('data-motion-speed')) || 0.15
      });
    });

    each(scope, '[data-motion-progress]', function (el) {
      progress.push(el);
    });

    if (revealEntries.length || parallax.length || progress.length) startScrollLoop();
  }

  initWithin(document);

  // Scroll restoration can land after a deferred script has run.
  window.addEventListener('load', frame);

  // The theme editor re-renders a section in place.
  document.addEventListener('shopify:section:load', function (event) {
    initWithin(event.target);
  });
})();
